"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AnimatePresence, motion } from 'framer-motion';
import { Spin as Hamburger } from 'hamburger-react';
import { ArrowRight, BookOpen, Box, CalendarClock, Captions, CircleHelp, CopyCheck, FileText, Gamepad, Gem, Layers3, LineChart, Newspaper, Trophy, UserCog, Waypoints, User, Package, Settings, MessageSquare } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import Icons from '../ui/icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import Image from "next/image";
import { Skeleton } from "../ui/skeleton";
import { signOut, useSession } from "next-auth/react";
import { roles } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import CartSheetContent from "../contents/CartSheetContent";
import { useCart } from "../providers/CartContext";

const MobileNavbar = () => {
    const { data: session, status } = useSession()
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { items } = useCart()
    const variants = {
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: "100%" },
    };

    useEffect(() => {
        const handleBodyOverflow = () => {
            document.body.style.overflow = isOpen ? 'hidden' : 'auto';
        };

        handleBodyOverflow();

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    return (
        <div className="relative flex w-full h-16 backdrop-blur-md lg:hidden">
            <div className="relative flex items-center justify-between w-full px-5">

                <Link href="/" className="text-lg  text-gray-300">
                    بازی تور
                </Link>

                <div className="relative flex gap-2 z-[99999] items-center">
                    <Sheet>
                        <SheetTrigger className="relative">
                            <div className='border-[#4e2b754f]  rounded-xl bg-[#3c2a4d80] p-2 border-[1px] '>
                                <Icons.cart className="w-[21px] h-[21px]" />
                            </div>
                            {items.length > 0 && (
                                <span className="absolute -top-1 -right-1 text-xs bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                                    <p className='text-xs pt-[2px]'>{items.length}</p>
                                </span>
                            )}
                        </SheetTrigger>
                        <SheetContent className="bg-background p-4 w-full max-w-2xl z-[1000]">
                            <SheetTitle>سبد خرید</SheetTitle>
                            <CartSheetContent />
                        </SheetContent>
                    </Sheet>
                    {status === 'loading' ? (
                        <div className="flex gap-2 items-center space-x-4">
                            <Skeleton className="h-10 w-10 rounded-full" />
                        </div>
                    ) : (
                        <>
                            {status === 'authenticated' ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger className='cursor-pointer rounded-2xl p-1' asChild>
                                        <div className="flex gap-2 items-center justify-center space-x-4">
                                            <Image src={session?.user?.profile || `/images/2.png`} alt='عکس پروفایل' width={1000} height={1000} className='h-10 w-10 p-1 border-[1px] border-[#4e2b754f]  rounded-xl bg-[#3c2a4d80]' />
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56 border-b-[1px] border-[#1C1B1D]">
                                        <DropdownMenuLabel>
                                            <div className="">
                                                <div className=" w-[120px] text-sm"><p>{session.user.username}</p></div>
                                                <div className=" w-[160px] text-xs"><p>بعنوان {roles[session.user.role || 'user']} وارد شدید</p></div>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuGroup>
                                            <DropdownMenuItem asChild>
                                                <Link href="/dashboard" className="flex items-center">
                                                    <User className="w-4 h-4 ml-2" />
                                                    داشبورد
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link href="/dashboard/orders/list" className="flex items-center">
                                                    <Package className="w-4 h-4 ml-2" />
                                                    سفارشات
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link href="/dashboard/profile" className="flex items-center">
                                                    <User className="w-4 h-4 ml-2" />
                                                    پروفایل
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link href="/dashboard/support/request" className="flex items-center">
                                                    <MessageSquare className="w-4 h-4 ml-2" />
                                                    تیکت پشتیبانی
                                                </Link>
                                            </DropdownMenuItem>
                                        </DropdownMenuGroup>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <a
                                                href="https://t.me/your_telegram"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center"
                                            >
                                                <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.2-.04-.28-.02-.12.02-1.98 1.26-5.61 3.71-.53.36-1.01.53-1.46.52-.48-.01-1.4-.27-2.08-.5-.84-.28-1.51-.43-1.45-.9.03-.25.38-.51 1.05-.78 4.12-1.79 6.87-2.97 8.26-3.54 3.93-1.63 4.75-1.91 5.42-1.91.12 0 .38.03.55.17.14.12.18.28.2.45-.02.05-.02.31-.17 1.08z" />
                                                </svg>
                                                تلگرام
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <a
                                                href="https://instagram.com/your_instagram"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center"
                                            >
                                                <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                                </svg>
                                                اینستاگرام
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => signOut()}>
                                            خروج
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <div className='flex flex-row gap-2'>
                                    <Button size="sm" variant="secondary" asChild>
                                        <Link href="/auth/signin">
                                            ورود
                                        </Link>
                                    </Button>
                                    <Button size="sm" variant="secondary" asChild>
                                        <Link href="/auth/signup">
                                            ثبت نام
                                        </Link>
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                    <Hamburger
                        toggled={isOpen}
                        toggle={setIsOpen}
                        color="#fff"
                        direction='left'
                        distance='md'
                        size={20}
                        rounded
                    />


                </div>

                <AnimatePresence presenceAffectsLayout>
                    {isOpen && (
                        <motion.div
                            initial="closed"
                            animate={isOpen ? "open" : "closed"}
                            variants={variants}
                            transition={{
                                type: "spring",
                                bounce: 0.15,
                                duration: 0.5,
                            }}
                            className="absolute z-[9999] rounded-lg w-full min-h-screen h-lvh flex flex-col inset-0 bg-background"
                        >
                            <ul className="flex flex-col items-start flex-1 w-full px-6 py-3 pt-20 space-y-3">
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/store" className="flex items-center w-full text-start">
                                        <Gem className="w-4 h-4 ml-2" />
                                        فروشگاه بازی
                                    </Link>
                                </li>
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="item-1" className="border-transparent">
                                        <AccordionTrigger className="px-4 py-2 text-base font-normal">
                                            <span className="flex items-center">
                                                <Gamepad className="w-4 h-4 ml-2" />
                                                بازی‌ها
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent onClick={() => setIsOpen(false)} className="flex flex-col items-start gap-1 mt-1">
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/online" className="flex items-center w-full text-start">
                                                    <Captions className="w-4 h-4 ml-2" />
                                                    بازی‌های آنلاین
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/story-mode" className="flex items-center w-full text-start">
                                                    <CalendarClock className="w-4 h-4 ml-2" />
                                                    بازی‌های داستانی
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/racing" className="flex items-center w-full text-start">
                                                    <LineChart className="w-4 h-4 ml-2" />
                                                    بازی‌های مسابقه‌ای
                                                </Link>
                                            </li>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/blog" className="flex items-center w-full text-start">
                                        <Newspaper className="w-4 h-4 ml-2" />
                                        بلاگ گیمینگ
                                    </Link>
                                </li>
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/tournaments" className="flex items-center w-full text-start">
                                        <Trophy className="w-4 h-4 ml-2" />
                                        تورنمنت‌ها
                                    </Link>
                                </li>
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="item-1" className="border-transparent">
                                        <AccordionTrigger className="px-4 py-2 text-base font-normal">
                                            <span className="flex items-center">
                                                <Layers3 className="w-4 h-4 ml-2" />
                                                منابع
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent onClick={() => setIsOpen(false)} className="flex flex-col items-start gap-1 mt-1">
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/resources/guides" className="flex items-center w-full text-start">
                                                    <BookOpen className="w-4 h-4 ml-2" />
                                                    راهنماها
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/resources/reviews" className="flex items-center w-full text-start">
                                                    <FileText className="w-4 h-4 ml-2" />
                                                    بررسی‌ها
                                                </Link>
                                            </li>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </ul>
                        </motion.div>

                    )}
                </AnimatePresence>

            </div>
        </div>
    )
};

export default MobileNavbar
